import { type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Bookmark,
  Check,
  ChevronRight,
  CircleDot,
  Clock,
  Copy,
  Flag,
  GitBranch,
  Globe2,
  Heart,
  Image as ImageIcon,
  List,
  Loader,
  MessageCircle,
  MoreHorizontal,
  PencilLine,
  Reply,
  Save,
  Search,
  Send,
  Share2,
  SlidersHorizontal,
  Smile,
  ThumbsDown,
  Trash2,
  X,
} from "lucide-react";
import PostShareModal from "../components/PostShareModal";
import {
  createComment,
  deleteComment,
  fetchComments,
  toggleCommentReaction,
  updateComment,
  type Comment,
} from "../api/comments";
import { deletePost, fetchPost, toggleReaction, updatePost } from "../api/posts";
import { createSpaceReport } from "../api/spaces";
import type { Post } from "../types";
import { formatBlackspaceDateTime } from "../utils/time";
import { hasIdentity, requireIdentity } from "../utils/identityGate";

const SORTS = [
  { id: "all", label: "All" },
  { id: "most-liked", label: "Top" },
  { id: "newest", label: "Newest" },
];
const COMMENT_REFINEMENTS = [
  { id: "all", label: "Everything" },
  { id: "threads", label: "Threads" },
  { id: "loved", label: "Loved" },
  { id: "mine", label: "Mine" },
  { id: "author", label: "Author" },
];
const COMMENT_REPLY_PAGE_SIZE = 3;
const DEFAULT_COMMENTS_ACCENT = "#a855f7";

type CommentsStyleVars = CSSProperties & {
  "--comments-accent"?: string;
  "--comments-accent-rgb"?: string;
  "--comments-accent-soft"?: string;
  "--comments-accent-border"?: string;
};

function SortIcon({ id }: { id: string }) {
  switch (id) {
    case "newest":
      return <Clock size={15} />;
    case "all":
      return <List size={15} />;
    case "oldest":
      return <CircleDot size={15} />;
    case "most-liked":
      return <Heart size={15} />;
    case "replies":
      return <GitBranch size={15} />;
    default:
      return <MessageCircle size={15} />;
  }
}

function RefinementIcon({ id }: { id: string }) {
  switch (id) {
    case "threads":
      return <GitBranch size={15} />;
    case "loved":
      return <Heart size={15} />;
    case "mine":
      return <CircleDot size={15} />;
    case "author":
      return <Check size={15} />;
    default:
      return <MessageCircle size={15} />;
  }
}

function getCurrentUsername(): string {
  try {
    const raw = localStorage.getItem("user");
    if (raw) return JSON.parse(raw)?.username || "";
  } catch {}
  return "";
}

function cleanUsername(value?: string | null) {
  return String(value || "").trim().replace(/^@+/, "");
}

function normalizeHexColor(value?: string | null, fallback = DEFAULT_COMMENTS_ACCENT) {
  const clean = String(value || "").trim();
  return /^#[0-9a-fA-F]{6}$/.test(clean) ? clean : fallback;
}

function hexToRgbChannels(hex: string) {
  const clean = normalizeHexColor(hex).slice(1);
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16),
  ].join(", ");
}

function alphaHex(hex: string, alpha: number) {
  const clean = normalizeHexColor(hex);
  const clamped = Math.max(0, Math.min(1, alpha));
  return `${clean}${Math.round(clamped * 255).toString(16).padStart(2, "0")}`;
}

function isBlackspacePost(post?: Post | null) {
  const id = String(post?.space?.id || "").trim().toLowerCase();
  const name = String(post?.space?.name || "").trim().toLowerCase();
  return !id || id === "blackspace" || name === "blackspace";
}

type UIComment = Comment & {
  pending?: boolean;
  error?: string;
};

type ReplyInsertionEvent = {
  eventId: number;
  parentId: string;
  comment?: UIComment;
  replaceId?: string;
  removeId?: string;
};

type ReportTarget =
  | { type: "post"; id: string; title: string }
  | { type: "comment"; id: string; title: string };

function ReportModal({
  target,
  onClose,
  onSubmit,
}: {
  target: ReportTarget | null;
  onClose: () => void;
  onSubmit: (reason: string, details: string, severity: "low" | "medium" | "high") => Promise<void>;
}) {
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [severity, setSeverity] = useState<"low" | "medium" | "high">("medium");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!target) return;
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onClose, target]);

  useEffect(() => {
    if (!target) {
      setReason("");
      setDetails("");
      setSeverity("medium");
      setSubmitting(false);
      setError("");
    }
  }, [target]);

  if (!target) return null;

  const submit = async () => {
    const cleanReason = reason.trim();
    if (cleanReason.length < 3) {
      setError("Give a clear reason first.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await onSubmit(cleanReason, details.trim(), severity);
      onClose();
    } catch (err: any) {
      setError(err?.message || "Report could not be sent.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/65 px-4 backdrop-blur-md" onMouseDown={onClose}>
      <div
        className="w-full max-w-lg rounded-xl border border-amber-300/20 bg-neutral-950/90 p-5 text-white shadow-[0_0_70px_rgba(251,191,36,0.12)]"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.24em] text-amber-200">Report</p>
            <h2 className="mt-2 text-2xl font-black">{target.title}</h2>
            <p className="mt-1 text-sm text-neutral-400">A reason is required so moderators get useful context.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-lg border border-neutral-700 bg-neutral-900 text-neutral-300 transition hover:border-amber-300/40 hover:text-white"
          >
            <X size={18} />
          </button>
        </div>

        <label className="block">
          <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-neutral-500">Reason</span>
          <input
            value={reason}
            onChange={(event) => setReason(event.target.value.slice(0, 120))}
            placeholder="Spam, abuse, private information..."
            className="w-full rounded-lg border border-neutral-800 bg-black/50 px-3 py-2 text-sm text-white outline-none transition placeholder:text-neutral-600 focus:border-amber-300/50"
          />
        </label>

        <label className="mt-4 block">
          <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-neutral-500">Details</span>
          <textarea
            value={details}
            onChange={(event) => setDetails(event.target.value.slice(0, 1000))}
            rows={4}
            placeholder="Add any context the reviewer should know..."
            className="stealth-scroll w-full resize-none rounded-lg border border-neutral-800 bg-black/50 px-3 py-2 text-sm leading-6 text-white outline-none transition placeholder:text-neutral-600 focus:border-amber-300/50"
          />
        </label>

        <div className="mt-4 flex flex-wrap gap-2">
          {(["low", "medium", "high"] as const).map((item) => (
            <button
              key={item}
              type="button"
              onClick={() => setSeverity(item)}
              className={`rounded-lg border px-3 py-2 text-xs font-black capitalize transition ${
                severity === item
                  ? "border-amber-300/40 bg-amber-300/15 text-amber-100"
                  : "border-neutral-800 bg-neutral-950 text-neutral-400 hover:border-amber-300/25 hover:text-white"
              }`}
            >
              {item}
            </button>
          ))}
        </div>

        {error && <p className="mt-4 rounded-lg border border-red-400/20 bg-red-500/10 p-3 text-sm text-red-200">{error}</p>}

        <div className="mt-5 flex justify-end gap-2 border-t border-neutral-800 pt-4">
          <button type="button" onClick={onClose} className="rounded-lg border border-neutral-700 bg-neutral-900/70 px-4 py-2 text-sm font-semibold text-neutral-300 transition hover:text-white">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || reason.trim().length < 3}
            className="inline-flex items-center gap-2 rounded-lg border border-amber-300/30 bg-amber-300/15 px-5 py-2 text-sm font-black text-amber-100 transition hover:border-amber-200/60 hover:bg-amber-300/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? <Loader size={16} className="animate-spin" /> : <Flag size={16} />}
            Send report
          </button>
        </div>
      </div>
    </div>
  );
}

function CommentComposer({
  label,
  placeholder,
  autoFocus,
  onSubmit,
  onCancel,
  docked = false,
  identityInitial = "B",
  replyTarget = null,
}: {
  label: string;
  placeholder: string;
  autoFocus?: boolean;
  onSubmit: (text: string) => Promise<void>;
  onCancel?: () => void;
  docked?: boolean;
  identityInitial?: string;
  replyTarget?: UIComment | null;
}) {
  const [text, setText] = useState("");
  const [focused, setFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (autoFocus || (docked && replyTarget)) textareaRef.current?.focus();
  }, [autoFocus, docked, replyTarget]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !text.trim()) onCancel?.();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [onCancel, text]);

  const submit = async () => {
    const next = text.trim();
    if (!next || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit(next);
      setText("");
      setFocused(false);
    } finally {
      setSubmitting(false);
    }
  };

  const sendOnEnter = (event: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey || event.nativeEvent.isComposing) return;
    event.preventDefault();
    submit();
  };

  if (docked) {
    const replyName = replyTarget?.author.displayName || replyTarget?.author.username || "Anonymous";
    return (
      <div className={`bs-comments-composer-stack ${focused || text || replyTarget ? "is-active" : ""}`}>
        {replyTarget && (
          <div className="bs-comments-reply-target">
            <span>
              <strong>Replying to {replyName}</strong>
              <small>{replyTarget.content}</small>
            </span>
            <button type="button" onClick={onCancel} aria-label="Cancel reply">
              <X size={15} />
            </button>
          </div>
        )}
        <div className="bs-comments-composer-dock">
          <div className="bs-comments-composer-avatar" aria-hidden="true">{identityInitial}</div>
          <textarea
            ref={textareaRef}
            value={text}
            onFocus={() => setFocused(true)}
            onChange={(event) => setText(event.target.value.slice(0, 1000))}
            onKeyDown={sendOnEnter}
            rows={1}
            placeholder={replyTarget ? `Reply to ${replyName}...` : placeholder}
            aria-label={label}
            className="stealth-scroll bs-comments-composer-input"
          />
          <div className="bs-comments-composer-tools" aria-label="Comment tools">
            <button type="button" title="Add image" aria-label="Add image" className="bs-comments-tool-button"><ImageIcon size={16} /></button>
            <button type="button" title="Add GIF" aria-label="Add GIF" className="bs-comments-tool-button bs-comments-gif-button">GIF</button>
            <button type="button" title="Emoji" aria-label="Emoji" className="bs-comments-tool-button"><Smile size={16} /></button>
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={!text.trim() || submitting}
            title="Send"
            aria-label={replyTarget ? "Send reply" : "Send comment"}
            className="bs-comments-send-button"
          >
            {submitting ? <Loader size={16} className="animate-spin" /> : <Send size={17} />}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bs-comments-reply-composer ${focused || text ? "is-active" : ""}`}>
      <div className="mb-2 flex items-center justify-between gap-3">
        <span className="truncate text-xs font-semibold text-neutral-300">{label}</span>
        {(focused || text) && <span className="shrink-0 text-[10px] font-medium text-neutral-500">{text.length}/1000</span>}
      </div>
      <textarea
        ref={textareaRef}
        value={text}
        onFocus={() => setFocused(true)}
        onChange={(event) => setText(event.target.value.slice(0, 1000))}
        onKeyDown={sendOnEnter}
        rows={focused || text ? 3 : 2}
        placeholder={placeholder}
        className="stealth-scroll w-full resize-none rounded-xl border border-white/10 bg-black/35 px-3 py-2.5 text-sm leading-6 text-neutral-100 outline-none transition placeholder:text-neutral-600 focus:border-violet-300/45"
      />
      {(focused || text || onCancel) && (
        <div className="mt-2 flex items-center justify-end gap-2">
          {onCancel && (
            <button type="button" onClick={onCancel} className="rounded-full border border-white/10 bg-neutral-950 px-3 py-1.5 text-xs font-bold text-neutral-400 transition hover:border-neutral-600 hover:text-white">
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={!text.trim() || submitting}
            className="inline-flex items-center gap-2 rounded-full border border-violet-300/30 bg-violet-300/15 px-3.5 py-1.5 text-xs font-black text-violet-100 transition hover:border-violet-200/60 hover:bg-violet-300/20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? <Loader size={13} className="animate-spin" /> : <Send size={13} />}
            Send
          </button>
        </div>
      )}
    </div>
  );
}

function CommentCard({
  comment,
  post,
  level = 0,
  onChanged,
  onDeleted,
  onReport,
  onReply,
  viewerUsername,
  replyInsertion,
  readOnly = false,
}: {
  comment: UIComment;
  post: Post;
  level?: number;
  onChanged: (comment: UIComment) => void;
  onDeleted: (comment: UIComment, deletedCount: number) => void;
  onReport: (comment: UIComment) => void;
  onReply: (comment: UIComment) => void;
  viewerUsername?: string;
  replyInsertion?: ReplyInsertionEvent | null;
  readOnly?: boolean;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState(comment.content);
  const [replies, setReplies] = useState<UIComment[]>([]);
  const [repliesLoaded, setRepliesLoaded] = useState(false);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [showRepliesExpanded, setShowRepliesExpanded] = useState(false);
  const [replyPage, setReplyPage] = useState(1);
  const [repliesHasMore, setRepliesHasMore] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [reacting, setReacting] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const name = comment.author.displayName || comment.author.username || "Anonymous";
  const initial = name.slice(0, 1).toUpperCase();
  const isEdited = Boolean(comment.editedAt && comment.editedAt !== comment.createdAt);
  const canEditComment = Boolean(
    comment.canEdit ||
    (viewerUsername && cleanUsername(comment.author.username).toLowerCase() === cleanUsername(viewerUsername).toLowerCase())
  );
  const resolvedDepth = Number.isFinite(Number(comment.depth)) ? Number(comment.depth) : level;
  const canReply = Boolean(comment.canReply !== false && resolvedDepth < 2 && level < 2);
  const mentionName = level >= 2
    ? comment.replyToAuthor?.displayName || comment.replyToAuthor?.username || ""
    : "";
  const heartCount = Number(comment.reactions?.love || comment.reactions?.like || 0);

  useEffect(() => {
    setEditText(comment.content);
  }, [comment.content]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [menuOpen]);

  const loadReplies = useCallback(async () => {
    if (loadingReplies) return;
    setLoadingReplies(true);
    try {
      const data = await fetchComments(String(post.id), { parent: comment.id, sort: "oldest", limit: COMMENT_REPLY_PAGE_SIZE, page: 1 });
      setReplies(data.comments);
      setRepliesLoaded(true);
      setReplyPage(2);
      setRepliesHasMore(data.hasMore || Number(comment.replyCount || 0) > data.comments.length);
    } finally {
      setLoadingReplies(false);
    }
  }, [comment.id, comment.replyCount, loadingReplies, post.id]);

  useEffect(() => {
    if (!canReply || !comment.replyCount || repliesLoaded || loadingReplies) return;
    loadReplies();
  }, [canReply, comment.replyCount, loadReplies, loadingReplies, repliesLoaded]);

  const loadMoreReplies = async () => {
    if (loadingReplies) return;
    setLoadingReplies(true);
    try {
      const data = await fetchComments(String(post.id), { parent: comment.id, sort: "oldest", limit: COMMENT_REPLY_PAGE_SIZE, page: replyPage });
      setReplies((current) => [...current, ...data.comments]);
      setReplyPage((current) => current + 1);
      setRepliesHasMore(data.hasMore);
    } finally {
      setLoadingReplies(false);
    }
  };

  useEffect(() => {
    if (!replyInsertion || replyInsertion.parentId !== comment.id) return;
    setRepliesLoaded(true);
    setShowRepliesExpanded(true);
    setReplies((current) => {
      if (replyInsertion.removeId) {
        return current.filter((item) => item.id !== replyInsertion.removeId);
      }
      if (!replyInsertion.comment) return current;
      const insertedComment = replyInsertion.comment;
      if (replyInsertion.replaceId) {
        let replaced = false;
        const next = current.map((item) => {
          if (item.id !== replyInsertion.replaceId) return item;
          replaced = true;
          return insertedComment;
        });
        return replaced ? next : [...current, insertedComment];
      }
      if (current.some((item) => item.id === insertedComment.id)) return current;
      return [...current, insertedComment];
    });
  }, [comment.id, replyInsertion]);

  const saveEdit = async () => {
    const next = editText.trim();
    if (!next || savingEdit) return;
    setSavingEdit(true);
    try {
      const updated = await updateComment(comment.id, next);
      onChanged({ ...comment, ...updated });
      setEditing(false);
    } finally {
      setSavingEdit(false);
    }
  };

  const copyComment = async () => {
    await navigator.clipboard.writeText(comment.content);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1000);
  };

  const reportComment = async () => {
    setMenuOpen(false);
    onReport(comment);
  };

  const removeComment = async () => {
    const confirmed = window.confirm("Delete this response?");
    if (!confirmed) return;
    await deleteComment(comment.id);
    onDeleted(comment, 1 + (comment.replyCount || 0));
  };

  const react = async (type: "like" | "love") => {
    if (readOnly) {
      requireIdentity("react to comments");
      return;
    }
    if (reacting) return;
    if (comment.pending || comment.error) return;
    setReacting(type);
    try {
      const result = await toggleCommentReaction(comment.id, type);
      onChanged({ ...comment, reactions: result.reactions, userReaction: result.userReaction });
    } finally {
      setReacting(null);
    }
  };
  const visibleReplies = showRepliesExpanded ? replies : replies.slice(0, 1);
  const totalVisibleReplyCount = Math.max(Number(comment.replyCount || 0), replies.length);
  const hiddenReplyCount = Math.max(0, totalVisibleReplyCount - visibleReplies.length);
  const canShowMoreReplies = showRepliesExpanded ? repliesHasMore : hiddenReplyCount > 0;

  return (
    <article className={`bs-comment-node relative ${level ? "is-reply" : "is-root"}`}>
      <div className={`bs-comment-card relative overflow-visible rounded-[1.35rem] border p-3.5 shadow-[0_18px_55px_rgba(0,0,0,0.24)] ${
        comment.error
          ? "border-red-400/25 bg-red-500/10"
          : comment.pending
            ? "border-cyan-300/20 bg-cyan-300/5 opacity-80"
            : level
              ? "border-white/[0.08] bg-black/30 shadow-none"
              : "border-white/10 bg-neutral-950/72"
      }`}>
        <span className="pointer-events-none absolute inset-x-5 top-0 h-px bg-gradient-to-r from-transparent via-white/18 to-transparent" />
        <div className="flex items-start gap-3">
          {comment.author.avatarUrl ? (
            <img src={comment.author.avatarUrl} alt="" className="bs-comment-avatar h-10 w-10 rounded-2xl border border-white/10 object-cover" />
          ) : (
            <div className="bs-comment-avatar grid h-10 w-10 place-items-center rounded-2xl border border-white/10 bg-neutral-900 text-sm font-black text-cyan-200">
              {initial}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="bs-comment-meta flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
              <p className="max-w-full truncate text-sm font-semibold text-white">{name}</p>
              <span className="text-[11px] text-neutral-500">@{comment.author.username}</span>
              <span className="text-[11px] text-neutral-600">{formatBlackspaceDateTime(comment.createdAt)}</span>
              {isEdited && <span className="rounded-full border border-white/10 px-2 py-0.5 text-[10px] font-medium text-neutral-400">edited</span>}
              {comment.pending && <span className="rounded-full border border-cyan-300/20 px-2 py-0.5 text-[10px] font-medium text-cyan-200">sending</span>}
              {comment.error && <span className="rounded-full border border-red-300/20 px-2 py-0.5 text-[10px] font-medium text-red-200">{comment.error}</span>}
            </div>

            {editing ? (
              <div className="mt-3">
                <textarea
                  value={editText}
                  onChange={(event) => setEditText(event.target.value.slice(0, 1000))}
                  rows={3}
                  className="stealth-scroll w-full resize-none rounded-2xl border border-cyan-300/30 bg-black/45 px-3.5 py-3 text-sm leading-6 text-white outline-none"
                />
                <div className="mt-2 flex justify-end gap-2">
                  <button type="button" onClick={() => setEditing(false)} className="rounded-full border border-white/10 px-3 py-1.5 text-xs font-bold text-neutral-400 hover:text-white">
                    Cancel
                  </button>
                  <button type="button" onClick={saveEdit} disabled={savingEdit || !editText.trim()} className="inline-flex items-center gap-2 rounded-full border border-cyan-300/30 bg-cyan-300/15 px-3 py-1.5 text-xs font-black text-cyan-100 disabled:opacity-50">
                    {savingEdit ? <Loader size={13} className="animate-spin" /> : <Save size={13} />}
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <p className="bs-comment-text mt-3 whitespace-pre-wrap break-words text-[15px] leading-7 text-neutral-200">
                {mentionName && <span className="bs-comment-mention">@{mentionName}</span>}
                {comment.content}
              </p>
            )}

            <div className="bs-comment-actions mt-4 flex flex-wrap items-center gap-2 text-xs">
              {canReply && !readOnly && !comment.pending && !comment.error && (
                <button
                  type="button"
                  onClick={() => onReply({ ...comment, depth: resolvedDepth })}
                  title="Reply"
                  aria-label="Reply to this comment"
                  className="inline-flex h-8 min-w-8 items-center justify-center rounded-full border border-white/10 px-2 font-bold text-neutral-400 transition hover:-translate-y-0.5 hover:border-cyan-300/30 hover:text-cyan-200 hover:shadow-[0_0_22px_rgba(34,211,238,0.12)]"
                >
                  <Reply size={12} />
                  <span>Reply</span>
                </button>
              )}
            </div>
          </div>

          {!comment.pending && !comment.error && (
          <div className="bs-comment-right-actions">
            <button
              type="button"
              onClick={() => react("love")}
              disabled={Boolean(reacting)}
              className={`bs-comment-heart-button ${comment.userReaction === "love" ? "is-active" : ""}`}
              title={`Love${heartCount ? ` (${heartCount})` : ""}`}
              aria-label={`Love comment${heartCount ? `, ${heartCount}` : ""}`}
            >
              <Heart size={14} className={comment.userReaction === "love" ? "fill-current" : ""} />
              {heartCount > 0 && <span>{heartCount}</span>}
            </button>
            <div ref={menuRef} className="relative shrink-0">
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                className="bs-comment-menu-button grid h-8 w-8 place-items-center rounded-full border border-transparent text-neutral-500 transition hover:border-white/10 hover:bg-white/[0.03] hover:text-white"
                aria-label="Comment options"
              >
                <MoreHorizontal size={16} />
              </button>
              {menuOpen && (
                <div className="bs-menu absolute right-0 top-10 z-20 w-40 overflow-hidden rounded-2xl border shadow-2xl shadow-black/40">
                  {canEditComment && (
                    <button type="button" onClick={() => { setEditing(true); setMenuOpen(false); }} className="bs-menu-item flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold">
                      <PencilLine size={14} className="text-cyan-300" />
                      Edit
                    </button>
                  )}
                  <button type="button" onClick={copyComment} className="bs-menu-item flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold">
                    {copied ? <Check size={14} className="text-emerald-300" /> : <Copy size={14} className="text-cyan-300" />}
                    {copied ? "Done" : "Copy"}
                  </button>
                  {comment.canDelete && (
                    <button type="button" onClick={removeComment} className="bs-menu-item flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold">
                      <Trash2 size={14} className="text-red-300" />
                      Delete
                    </button>
                  )}
                  {comment.canReport && (
                    <button type="button" onClick={reportComment} className="bs-menu-item flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold">
                      <Flag size={14} className="text-amber-300" />
                      Report
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
          )}
        </div>
      </div>

      {repliesLoaded && replies.length > 0 && (
        <div className="bs-comment-replies mt-3 rounded-[1.35rem] border border-white/[0.08] bg-black/20 p-3">
          <div className="grid gap-2.5">
            {visibleReplies.map((reply) => (
              <CommentCard
                key={reply.id}
                comment={reply}
                post={post}
                level={level + 1}
                onChanged={(updated) => setReplies((current) => current.map((item) => item.id === updated.id ? updated : item))}
                onDeleted={(deleted, deletedCount) => {
                  setReplies((current) => current.filter((item) => item.id !== deleted.id));
                  onChanged({ ...comment, replyCount: Math.max(0, comment.replyCount - deletedCount) });
                }}
                onReport={onReport}
                onReply={onReply}
                viewerUsername={viewerUsername}
                replyInsertion={replyInsertion}
                readOnly={readOnly}
              />
            ))}
          </div>
          {canShowMoreReplies && (
            <button
              type="button"
              onClick={() => {
                if (!showRepliesExpanded) {
                  setShowRepliesExpanded(true);
                  return;
                }
                if (repliesHasMore) loadMoreReplies();
              }}
              disabled={loadingReplies}
              className="bs-comment-more-replies mt-3 inline-flex items-center gap-2 rounded-full border border-white/10 px-3 py-2 text-xs font-bold text-neutral-400 transition hover:border-cyan-300/30 hover:text-cyan-200 disabled:opacity-60"
            >
              {loadingReplies ? <Loader size={13} className="animate-spin" /> : <ChevronRight size={13} />}
              {showRepliesExpanded && repliesHasMore ? "View more replies" : `View ${hiddenReplyCount} more ${hiddenReplyCount === 1 ? "reply" : "replies"}`}
            </button>
          )}
        </div>
      )}
    </article>
  );
}

export default function PostDetail() {
  const { postId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUsername = getCurrentUsername();
  const isAnonymous = !hasIdentity();
  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<UIComment[]>([]);
  const [replyTarget, setReplyTarget] = useState<UIComment | null>(null);
  const [replyInsertion, setReplyInsertion] = useState<ReplyInsertionEvent | null>(null);
  const [sort, setSort] = useState("all");
  const [commentSearch, setCommentSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newCommentCount, setNewCommentCount] = useState(0);
  const [reportTarget, setReportTarget] = useState<ReportTarget | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [postMenuOpen, setPostMenuOpen] = useState(false);
  const [postCopied, setPostCopied] = useState(false);
  const [postEditing, setPostEditing] = useState(false);
  const [postEditText, setPostEditText] = useState("");
  const [postActionBusy, setPostActionBusy] = useState<string | null>(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [commentRefinement, setCommentRefinement] = useState("all");
  const postMenuRef = useRef<HTMLDivElement | null>(null);
  const filterPanelRef = useRef<HTMLDivElement | null>(null);
  const sourceCardRef = useRef<HTMLElement | null>(null);
  const postEditRef = useRef<HTMLTextAreaElement | null>(null);

  const backLabel = (location.state as any)?.label || "Feed";
  const blackspacePost = isBlackspacePost(post);
  const commentsAccent = useMemo(
    () => normalizeHexColor(blackspacePost ? DEFAULT_COMMENTS_ACCENT : post?.space?.accentColor, DEFAULT_COMMENTS_ACCENT),
    [blackspacePost, post?.space?.accentColor]
  );
  const commentsStyle = useMemo<CommentsStyleVars>(() => ({
    "--comments-accent": commentsAccent,
    "--comments-accent-rgb": hexToRgbChannels(commentsAccent),
    "--comments-accent-soft": alphaHex(commentsAccent, 0.16),
    "--comments-accent-border": alphaHex(commentsAccent, 0.34),
  }), [commentsAccent]);

  useEffect(() => {
    document.body.classList.add("page-no-scroll");
    return () => document.body.classList.remove("page-no-scroll");
  }, []);

  const goBack = useCallback(() => {
    const state = location.state as any;
    if (state?.from) {
      navigate(state.from, { state: { restoreFeed: Boolean(state.restoreFeed), postId } });
      return;
    }
    navigate("/home", { state: { restoreFeed: true, postId } });
  }, [location.state, navigate, postId]);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (filterOpen || postMenuOpen || reportTarget || shareOpen || postEditing) return;
      goBack();
    };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [filterOpen, goBack, postEditing, postMenuOpen, reportTarget, shareOpen]);

  const loadPost = useCallback(async () => {
    if (!postId) return;
    setLoading(true);
    setError(null);
    try {
      const next = await fetchPost(postId);
      setPost(next);
    } catch (err: any) {
      setError(err?.message || "Post could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [postId]);

  const loadComments = useCallback(async (reset = true) => {
    if (!postId) return;
    setCommentsLoading(true);
    try {
      const nextPage = reset ? 1 : page;
      const data = await fetchComments(postId, {
        page: nextPage,
        limit: 20,
        sort: sort === "all" ? "newest" : sort,
        repliesOnly: sort === "replies",
      });
      setComments((current) => reset ? data.comments : [...current, ...data.comments]);
      setPage(nextPage + 1);
      setHasMore(data.hasMore);
      setTotal(data.total);
    } finally {
      setCommentsLoading(false);
    }
  }, [page, postId, sort]);

  useEffect(() => {
    loadPost();
  }, [loadPost]);

  useEffect(() => {
    setPostEditText(post?.content || "");
  }, [post?.content]);

  useEffect(() => {
    if (!postEditing) return;
    window.requestAnimationFrame(() => {
      sourceCardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      postEditRef.current?.focus();
    });
  }, [postEditing]);

  useEffect(() => {
    loadComments(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postId, sort]);

  useEffect(() => {
    if (!postId) return;
    const timer = window.setInterval(async () => {
      if (document.hidden || commentsLoading) return;
      try {
        const data = await fetchComments(postId, {
          page: 1,
          limit: 1,
          sort: sort === "all" ? "newest" : sort,
          repliesOnly: sort === "replies",
        });
        if (data.total > total) setNewCommentCount(data.total - total);
      } catch {}
    }, 15000);
    return () => window.clearInterval(timer);
  }, [commentsLoading, postId, sort, total]);

  useEffect(() => {
    if (!postMenuOpen) return;
    const close = (event: MouseEvent) => {
      if (postMenuRef.current && !postMenuRef.current.contains(event.target as Node)) setPostMenuOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setPostMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [postMenuOpen]);

  useEffect(() => {
    if (!filterOpen) return;
    const close = (event: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target as Node)) setFilterOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFilterOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", escape);
    };
  }, [filterOpen]);

  const updateCommentInList = (updated: UIComment) => {
    setComments((current) => current.map((item) => item.id === updated.id ? updated : item));
  };

  const removeCommentFromList = (deleted: UIComment, deletedCount: number) => {
    setComments((current) => current.filter((item) => item.id !== deleted.id));
    setTotal((current) => Math.max(0, current - deletedCount));
    setPost((current) => current ? {
      ...current,
      reactions: { ...(current.reactions || {}), comment: Math.max(0, Number(current.reactions?.comment || 0) - deletedCount) },
    } : current);
  };

  const bumpPostCommentCount = (delta: number) => {
    setTotal((current) => Math.max(0, current + delta));
    setPost((current) => current ? {
      ...current,
      reactions: { ...(current.reactions || {}), comment: Math.max(0, Number(current.reactions?.comment || 0) + delta) },
    } : current);
  };

  const publishReplyInsertion = (event: Omit<ReplyInsertionEvent, "eventId">) => {
    setReplyInsertion({ ...event, eventId: Date.now() + Math.random() });
  };

  const addComment = async (text: string) => {
    if (isAnonymous) {
      requireIdentity("write a comment");
      return;
    }
    const replyingTo = replyTarget;
    setError("");
    if (replyingTo) {
      const parentId = String(replyingTo.id);
      const parentDepth = Number.isFinite(Number(replyingTo.depth)) ? Number(replyingTo.depth) : 0;
      const replyDepth = parentDepth + 1;
      const tempId = `pending-reply-${Date.now()}`;
      const optimistic: UIComment = {
        id: tempId,
        author: { userId: "me", username: currentUsername || "you", displayName: "You", avatarUrl: "" },
        content: text,
        parentComment: parentId,
        replyToAuthor: replyingTo.author,
        depth: replyDepth,
        canReply: replyDepth < 2,
        replyCount: 0,
        reactions: { like: 0, love: 0 },
        userReaction: null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        pending: true,
      };
      setReplyTarget(null);
      publishReplyInsertion({ parentId, comment: optimistic });
      setComments((current) => current.map((item) => item.id === parentId ? { ...item, replyCount: Number(item.replyCount || 0) + 1 } : item));
      bumpPostCommentCount(1);
      try {
        const created = await createComment(postId, text, parentId);
        publishReplyInsertion({ parentId, comment: created, replaceId: tempId });
      } catch (err: any) {
        publishReplyInsertion({ parentId, removeId: tempId });
        setComments((current) => current.map((item) => item.id === parentId ? { ...item, replyCount: Math.max(0, Number(item.replyCount || 0) - 1) } : item));
        bumpPostCommentCount(-1);
        setError(err?.message || "Reply failed.");
        window.setTimeout(() => setError((current) => current === (err?.message || "Reply failed.") ? "" : current), 3200);
      }
      return;
    }

    const tempId = `pending-comment-${Date.now()}`;
    const optimistic: UIComment = {
      id: tempId,
      author: { userId: "me", username: "you", displayName: "You", avatarUrl: "" },
      content: text,
      parentComment: null,
      replyCount: 0,
      reactions: { like: 0, love: 0 },
      userReaction: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      pending: true,
    };
    setComments((current) => sort === "oldest" ? [...current, optimistic] : [optimistic, ...current]);
    bumpPostCommentCount(1);
    try {
      const created = await createComment(postId, text);
      setComments((current) => current.map((item) => item.id === tempId ? created : item));
    } catch (err: any) {
      setComments((current) => current.map((item) => item.id === tempId ? { ...item, pending: false, error: err?.message || "Comment failed" } : item));
      bumpPostCommentCount(-1);
    }
  };

  const commentCount = useMemo(() => post?.reactions?.comment ?? total, [post?.reactions?.comment, total]);
  const threadCount = useMemo(
    () => comments.reduce((sum, item) => sum + Number(item.replyCount || 0), 0),
    [comments]
  );
  const visibleComments = useMemo(() => {
    const value = commentSearch.trim().toLowerCase();
    return comments.filter((comment) => {
      const authorUsername = cleanUsername(post?.author?.username).toLowerCase();
      const viewerUsername = cleanUsername(currentUsername).toLowerCase();
      const commentUsername = cleanUsername(comment.author.username).toLowerCase();
      if (commentRefinement === "threads" && Number(comment.replyCount || 0) <= 0) return false;
      if (commentRefinement === "loved" && Number(comment.reactions?.love || comment.reactions?.like || 0) <= 0) return false;
      if (commentRefinement === "mine" && commentUsername !== viewerUsername) return false;
      if (commentRefinement === "author" && (!authorUsername || commentUsername !== authorUsername)) return false;
      if (!value) return true;
      const authorName = comment.author.displayName || comment.author.username || "Anonymous";
      return [comment.content, authorName, comment.author.username]
        .filter(Boolean)
        .some((entry) => String(entry).toLowerCase().includes(value));
    });
  }, [commentRefinement, commentSearch, comments, currentUsername, post?.author?.username]);
  const currentInitial = useMemo(() => {
    const name = cleanUsername(currentUsername) || "B";
    return name.slice(0, 1).toUpperCase();
  }, [currentUsername]);

  const refreshNewComments = async () => {
    setNewCommentCount(0);
    await loadComments(true);
    await loadPost();
  };

  const postReact = async (type: string) => {
    if (!post || postActionBusy) return;
    if (isAnonymous) {
      requireIdentity(type === "save" ? "save posts" : "react to posts");
      return;
    }
    setPostActionBusy(type);
    try {
      const data = await toggleReaction(post.id, type);
      setPost({
        ...post,
        reactions: data.reactions || post.reactions,
        userReaction: data.userReaction ?? null,
        ...(typeof data.userSaved === "boolean" ? { userSaved: data.userSaved } : {}),
      });
    } finally {
      setPostActionBusy(null);
    }
  };

  const savePostEdit = async () => {
    if (!post || postActionBusy) return;
    const next = postEditText.trim();
    if (!next) return;
    setPostActionBusy("edit");
    setError(null);
    try {
      const updated = await updatePost(post.id, next);
      setPost({ ...post, ...updated });
      setPostEditing(false);
    } catch (err: any) {
      setError(err?.message || "Post edit failed.");
    } finally {
      setPostActionBusy(null);
    }
  };

  const beginPostEdit = () => {
    if (!post) return;
    setPostEditText(post.content || "");
    setPostEditing(true);
    setPostMenuOpen(false);
  };

  const copyPost = async () => {
    if (!post) return;
    await navigator.clipboard.writeText(post.content || "");
    setPostCopied(true);
    window.setTimeout(() => setPostCopied(false), 1000);
  };

  const removePost = async () => {
    if (!post) return;
    const confirmed = window.confirm("Delete this post?");
    if (!confirmed) return;
    setPostActionBusy("delete");
    try {
      await deletePost(post.id, "Deleted from post detail");
      goBack();
    } finally {
      setPostActionBusy(null);
    }
  };

  const submitReport = async (reason: string, details: string, severity: "low" | "medium" | "high") => {
    if (!reportTarget || !post) return;
    await createSpaceReport(post.space?.id || "blackspace", {
      targetType: reportTarget.type,
      targetId: reportTarget.id,
      reason,
      details,
      severity,
    });
  };

  return (
    <div
      className={`bs-comments-page bs-page relative flex h-screen flex-col overflow-hidden ${blackspacePost ? "is-blackspace-post" : "is-space-accented"}`}
      style={commentsStyle}
    >
      <main className="bs-comments-shell mx-auto flex w-full max-w-2xl flex-1 flex-col overflow-hidden px-3 pb-28 pt-4 sm:px-5">
        <header className="bs-comments-topbar">
          <button type="button" onClick={goBack} className="bs-comments-icon-button" aria-label={`Back to ${backLabel}`}>
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0 flex-1">
            <h1>Comments</h1>
            <p>{commentCount} {commentCount === 1 ? "comment" : "comments"} <span aria-hidden="true">•</span> {threadCount} in this thread</p>
          </div>
          <div ref={filterPanelRef} className="bs-comments-filter-menu-wrap">
            <button
              type="button"
              onClick={() => setFilterOpen((open) => !open)}
              className={`bs-comments-icon-button ${filterOpen ? "is-active" : ""}`}
              aria-label="Comment filters"
              aria-expanded={filterOpen}
              title="Filters"
            >
              <SlidersHorizontal size={17} />
            </button>
            {filterOpen && (
              <div className="bs-comments-filter-panel" role="dialog" aria-label="Comment filters">
                <div className="bs-comments-filter-panel-head">
                  <span>Refine comments</span>
                  <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters">
                    <X size={14} />
                  </button>
                </div>
                <div className="bs-comments-filter-options" role="listbox" aria-label="Refine comments">
                  {COMMENT_REFINEMENTS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        setCommentRefinement(item.id);
                        setFilterOpen(false);
                      }}
                      className={commentRefinement === item.id ? "is-active" : ""}
                      aria-pressed={commentRefinement === item.id}
                    >
                      <RefinementIcon id={item.id} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
                <p className="bs-comments-filter-hint">Sorting and comment search stay below the post.</p>
              </div>
            )}
          </div>
          {post && (
            <div ref={postMenuRef} className="relative shrink-0">
              <button
                type="button"
                onClick={() => setPostMenuOpen((open) => !open)}
                className="bs-comments-icon-button"
                aria-label="Post options"
                title="More"
              >
                <MoreHorizontal size={18} />
              </button>
              {postMenuOpen && (
                <div className="bs-menu bs-comments-post-menu absolute right-0 top-11 z-30 w-40 overflow-hidden rounded-xl border shadow-2xl shadow-black/40">
                  {post.canEdit && (
                    <button type="button" onClick={beginPostEdit} className="bs-menu-item flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold">
                      <PencilLine size={14} className="text-cyan-300" />
                      Edit
                    </button>
                  )}
                  <button type="button" onClick={copyPost} className="bs-menu-item flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold">
                    {postCopied ? <Check size={14} className="text-emerald-300" /> : <Copy size={14} className="text-cyan-300" />}
                    {postCopied ? "Copied" : "Copy"}
                  </button>
                  {post.canDelete && (
                    <button type="button" onClick={removePost} className="bs-menu-item flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold">
                      <Trash2 size={14} className="text-red-300" />
                      Delete
                    </button>
                  )}
                  {post.canReport && (
                    <button type="button" onClick={() => { setReportTarget({ type: "post", id: post.id, title: "Report post" }); setPostMenuOpen(false); }} className="bs-menu-item flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold">
                      <Flag size={14} className="text-amber-300" />
                      Report
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </header>

        {error && <div className="bs-comments-error" role="alert">{error}</div>}

        {loading ? (
          <div className="bs-comments-loading">
            <Loader size={20} className="animate-spin text-violet-300" />
          </div>
        ) : post ? (
          <>
            <section ref={sourceCardRef} className={`bs-comments-source-card ${postEditing ? "is-editing" : ""}`} aria-label="Original post">
              <div className="bs-comments-source-main">
                <div className="bs-comments-source-avatar">
                  {post.author?.avatarUrl ? <img src={post.author.avatarUrl} alt="" /> : <span>{(post.author?.displayName || post.author?.username || "A").slice(0, 1).toUpperCase()}</span>}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="bs-comments-source-author">
                    <strong>{post.author?.displayName || post.author?.username || "Anonymous"}</strong>
                    <Check size={13} aria-hidden="true" />
                    {post.space?.name && <span>{post.space.name}</span>}
                  </div>
                  <div className="bs-comments-source-meta">
                    <span>{formatBlackspaceDateTime(post.createdAt)}</span>
                    {post.editedAt && (
                      <>
                        <span aria-hidden="true">•</span>
                        <span className="bs-comments-edited-label">edited</span>
                      </>
                    )}
                    <span aria-hidden="true">•</span>
                    <Globe2 size={12} />
                    <span>{post.visibility === "private" ? "Private" : post.visibility === "followers" ? "Followers" : "Public"}</span>
                  </div>
                  {postEditing ? (
                    <div className="bs-comments-edit-post">
                      <textarea
                        ref={postEditRef}
                        value={postEditText}
                        onChange={(event) => setPostEditText(event.target.value.slice(0, 1000))}
                        rows={4}
                        className="stealth-scroll"
                      />
                      <div>
                        <button type="button" onClick={() => { setPostEditing(false); setPostEditText(post.content || ""); }}>Cancel</button>
                        <button type="button" onClick={savePostEdit} disabled={postActionBusy === "edit" || !postEditText.trim()}>
                          {postActionBusy === "edit" ? <Loader size={13} className="animate-spin" /> : <Save size={13} />}
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="bs-comments-source-text">{post.content}</p>
                  )}
                </div>
                <div className="bs-comments-source-art" aria-hidden="true">
                  <div />
                </div>
              </div>
              {!postEditing && (
                <div className="bs-comments-source-actions">
                  {post.allowedActions?.includes("love") !== false && (
                    <button type="button" onClick={() => postReact("love")} disabled={Boolean(postActionBusy)} className={post.userReaction === "love" ? "is-active" : ""} title="Love" aria-label="Love post">
                      <Heart size={18} /> <span>{post.reactions?.love || 0}</span>
                    </button>
                  )}
                  <button type="button" className="is-selected" title="Comments" aria-label="Comments">
                    <MessageCircle size={18} /> <span>{commentCount}</span>
                  </button>
                  {post.allowedActions?.includes("dislike") !== false && (
                    <button type="button" onClick={() => postReact("dislike")} disabled={Boolean(postActionBusy)} className={post.userReaction === "dislike" ? "is-active" : ""} title="Dislike" aria-label="Dislike post">
                      <ThumbsDown size={18} /> <span>{post.reactions?.dislike || 0}</span>
                    </button>
                  )}
                  {post.allowedActions?.includes("save") !== false && (
                    <button type="button" onClick={() => postReact("save")} disabled={Boolean(postActionBusy)} className={post.userSaved ? "is-active" : ""} title="Save" aria-label="Save post">
                      <Bookmark size={18} className={post.userSaved ? "fill-current" : ""} />
                    </button>
                  )}
                  <button type="button" onClick={() => setShareOpen(true)} title="Share" aria-label="Share post">
                    <Share2 size={18} />
                  </button>
                </div>
              )}
            </section>

            <div className="stealth-scroll bs-comments-discussion-scroll">
              <section className="bs-comments-controls" aria-label="Comment controls">
                <div className="bs-comments-tabs" role="tablist" aria-label="Comment sort">
                  {SORTS.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSort(item.id)}
                      className={sort === item.id ? "is-active" : ""}
                      title={item.label}
                      aria-label={`Show ${item.label}`}
                    >
                      <SortIcon id={item.id} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </div>
                <label className="bs-comments-search">
                  <Search size={16} />
                  <span className="sr-only">Search comments</span>
                  <input value={commentSearch} onChange={(event) => setCommentSearch(event.target.value)} placeholder="Search comments" />
                </label>
              </section>

              {newCommentCount > 0 && (
                <button type="button" onClick={refreshNewComments} className="bs-comments-new-button">
                  <MessageCircle size={15} />
                  {newCommentCount} new {newCommentCount === 1 ? "comment" : "comments"}
                </button>
              )}

              <section className="bs-comments-list" aria-label="Comments">
                {commentsLoading && comments.length === 0 ? (
                  <div className="bs-comments-loading is-list">
                    <Loader size={18} className="animate-spin text-violet-300" />
                  </div>
                ) : comments.length === 0 ? (
                  <div className="bs-comments-empty">
                    <MessageCircle size={24} />
                    <strong>No comments yet</strong>
                    <span>The first comment can set the tone.</span>
                  </div>
                ) : visibleComments.length === 0 ? (
                  <div className="bs-comments-empty">
                    <Search size={24} />
                    <strong>No matching comments</strong>
                    <span>Try a different word or username.</span>
                  </div>
                ) : (
                  visibleComments.map((comment) => (
                    <CommentCard
                      key={comment.id}
                      comment={comment}
                      post={post}
                      onChanged={updateCommentInList}
                      onDeleted={removeCommentFromList}
                      onReport={(comment) => setReportTarget({ type: "comment", id: comment.id, title: "Report comment" })}
                      onReply={setReplyTarget}
                      viewerUsername={currentUsername}
                      replyInsertion={replyInsertion}
                      readOnly={isAnonymous}
                    />
                  ))
                )}
              </section>

              {hasMore && (
                <div className="bs-comments-load-more">
                  <button type="button" onClick={() => loadComments(false)} disabled={commentsLoading}>
                    {commentsLoading ? <Loader size={15} className="animate-spin" /> : <MessageCircle size={15} />}
                    Load more comments
                  </button>
                </div>
              )}
            </div>
          </>
        ) : null}
      </main>
      {post && (isAnonymous ? (
        <footer className="bs-comments-composer-shell">
          <button type="button" onClick={() => requireIdentity("join the discussion")} className="mx-auto flex h-12 w-full max-w-2xl items-center justify-center rounded-xl border border-cyan-300/25 bg-cyan-300/10 px-4 text-sm font-black text-cyan-100 hover:bg-cyan-300/15">
            Create an identity to comment
          </button>
        </footer>
      ) : (
        <footer className="bs-comments-composer-shell">
          <CommentComposer
            label={replyTarget ? "Write a reply" : "Write a comment"}
            placeholder="Write a comment..."
            onSubmit={addComment}
            onCancel={() => setReplyTarget(null)}
            docked
            identityInitial={currentInitial}
            replyTarget={replyTarget}
          />
        </footer>
      ))}
      {post && <PostShareModal post={post} open={shareOpen} onClose={() => setShareOpen(false)} />}
      <ReportModal target={reportTarget} onClose={() => setReportTarget(null)} onSubmit={submitReport} />
    </div>
  );
}
